import dayjs from 'dayjs';

import { IInfoItem } from '@src/components/ui/InfoTable/interfaces';
import { DATE_FORMATS, GATEWAYS, IDateFormats } from '@src/constants';
import { ITFunction } from '@src/interfaces/locale.interfaces';
import { IGateway } from '@src/interfaces/subscriptions.interfaces';
import { IUserSubscription } from '@src/interfaces/user.interfaces';

import { checkForWebSiteGateway } from './payment.helpers';
import { subscriptionStatusSwitchHelper } from './subscriptions.helpers';

export const PAYMENT_SYSTEM_MAP: Record<'default' | IGateway, string> = {
  default: '',
  [GATEWAYS.APPLE_PAY]: 'Apple Pay',
  [GATEWAYS.GOOGLE_PAY]: 'Google Pay',
  [GATEWAYS.STRIPE]: 'FitFast Website',
  [GATEWAYS.MOLLIE]: 'FitFast Website',
  [GATEWAYS.PRIMER]: 'FitFast Website',
};

type IGetInfoTableListProps = {
  t: ITFunction;
  subscription?: IUserSubscription;
};

export const getActiveUntilDate = (
  subscription?: IUserSubscription,
  format: IDateFormats = DATE_FORMATS.YYYY_MM_DD_DASHED,
) => {
  const renewalDate = subscription?.next_payment
    ? dayjs(subscription?.next_payment).format(format)
    : undefined;

  return {
    renewalDate,
    renewalDateValue: !subscription?.is_canceled ? renewalDate : undefined,
    activeUntilDate: subscription?.ends_at
      ? dayjs(subscription?.ends_at).format(format)
      : undefined,
  };
};

export const getInfoTableList = ({
  t,
  subscription,
}: IGetInfoTableListProps): IInfoItem[] => {
  const { renewalDateValue, activeUntilDate, renewalDate, pause_ends_at } =
    subscription || {};

  const pauseEndsAt = dayjs(pause_ends_at);
  const subsActiveDays = renewalDate
    ? dayjs(renewalDate).diff(dayjs(), 'day') || 0
    : 0;

  const [statusTitle, statusTextClassName, dateClassName] =
    subscriptionStatusSwitchHelper(subscription, {
      isActive: ['subscriptionStatus.active', 'text-green-5', 'text-green-5'],
      isExpired: [
        'subscriptionStatus.expired',
        'text-orange-5',
        'text-orange-5',
      ],
      isCancelled: [
        'subscriptionStatus.cancelled',
        'text-orange-5',
        'text-orange-5',
      ],
      isPaused: ['subscriptionStatus.paused', 'text-orange-3', 'text-green-5'],
    }) || [];

  return [
    {
      label: t('manageSubscription.subscriptionStatus'),
      value: t(statusTitle || ''),
      className: statusTextClassName,
    },
    {
      label: t('manageSubscription.activeUntil'),
      value: activeUntilDate || renewalDateValue || t(statusTitle ?? ''),
      show: !subscription?.is_paused,
    },
    {
      label: t('manageSubscription.pausedOn'),
      value: pauseEndsAt
        .subtract(1, 'month')
        .format(DATE_FORMATS.YYYY_MM_DD_DASHED),
      show: subscription?.is_paused,
    },
    {
      label: t('manageSubscription.resumesOn'),
      value: pauseEndsAt.format(DATE_FORMATS.YYYY_MM_DD_DASHED),
      show: subscription?.is_paused,
      className: dateClassName,
    },
    {
      label: t('manageSubscription.renewalDate'),
      value: renewalDateValue || t(statusTitle ?? ''),
      show: !subscription?.is_paused,
      className: dateClassName,
    },
    {
      label: t('manageSubscription.nextRenewalDate'),
      subLabel: t('manageSubscription.daysLeft', {
        count: subsActiveDays,
      }),
      value: renewalDateValue || t(statusTitle ?? ''),
      show: subscription?.is_paused,
    },
    {
      label: t('manageSubscription.paymentMethod'),
      value: PAYMENT_SYSTEM_MAP[subscription?.gateway || 'default'],
    },
  ];
};

export const MANAGE_SUBSCRIPTION_ACTIONS_FACTORY_RESULT = {
  ON_WEBSITE: 'on-website',
  ON_APP: 'on-app',
} as const;

export type IManageSubscriptionActionsFactoryResult =
  (typeof MANAGE_SUBSCRIPTION_ACTIONS_FACTORY_RESULT)[keyof typeof MANAGE_SUBSCRIPTION_ACTIONS_FACTORY_RESULT];

export const manageSubscriptionActionsFactory = (
  subscription?: IUserSubscription,
): IManageSubscriptionActionsFactoryResult | 'default' => {
  if (!subscription) return 'default';
  if (checkForWebSiteGateway(subscription?.gateway))
    return MANAGE_SUBSCRIPTION_ACTIONS_FACTORY_RESULT.ON_WEBSITE;

  return MANAGE_SUBSCRIPTION_ACTIONS_FACTORY_RESULT.ON_APP;
};

type ISubscriptionGatewayKey = Extract<IGateway, 'apple_pay' | 'google_pay'>;

export const ON_APP_NOTIFICATION_STORE_MAP: Record<
  ISubscriptionGatewayKey | 'default',
  string
> = {
  default: '',
  [GATEWAYS.APPLE_PAY]: 'Apple App Store',
  [GATEWAYS.GOOGLE_PAY]: 'Google Play',
};
